import wx from 'weixin-js-sdk';
import StorageUtil from './storage-util';
import { SystemConst } from './constant';
import { reportH5MiniAuthState } from './auth-debug-reporter';

const MINI_RESTORE_FLAG = 'ysdhMiniRestoreTried';
const MINI_RESTORE_APPLIED_FLAG = 'ysdhMiniRestoreApplied';
const MINI_AUTH_SYNCED_PARAM = 'mp_auth_synced';
const MINI_WEBVIEW_PAGE = '/pages/frame/index';

function safeGet(key) {
  try {
    return StorageUtil.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeSessionGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeSessionSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {}
}

function safeSessionRemove(key) {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {}
}

function safeLocationSearch() {
  try {
    return window.location.search || '';
  } catch (e) {
    return '';
  }
}

function safeLocationHref() {
  try {
    return window.location.href || '';
  } catch (e) {
    return '';
  }
}

function getUrlParam(name) {
  try {
    const urlParams = new URLSearchParams(safeLocationSearch());
    return urlParams.has(name) ? urlParams.get(name) : null;
  } catch (e) {
    const search = safeLocationSearch().replace(/^\?/, '');
    const parts = search ? search.split('&') : [];
    for (let i = 0; i < parts.length; i++) {
      const item = parts[i];
      const equalIndex = item.indexOf('=');
      const key = equalIndex > -1 ? item.substring(0, equalIndex) : item;
      if (key === name) {
        const value = equalIndex > -1 ? item.substring(equalIndex + 1) : '';
        return decodeURIComponent(value);
      }
    }
    return null;
  }
}

function hasUrlParam(name) {
  return getUrlParam(name) !== null;
}

function removeUrlParamFallback(href, name) {
  return href
    .replace(new RegExp(`([?&])${name}=[^&]*&?`, 'g'), '$1')
    .replace(/[?&]$/, '')
    .replace(/\?&/g, '?');
}

function isMiniProgram(callback) {
  if (window.__wxjs_environment === 'miniprogram') {
    window.__ysdh_is_mini_program = true;
    callback(true);
    return;
  }

  if (wx && wx.miniProgram && wx.miniProgram.getEnv) {
    wx.miniProgram.getEnv(res => {
      window.__ysdh_is_mini_program = !!res.miniprogram;
      callback(!!res.miniprogram);
    });
    return;
  }

  callback(false);
}

function cleanCurrentUrl() {
  try {
    const url = new URL(safeLocationHref());
    url.searchParams.delete('auth_code_');
    url.searchParams.delete('mp_restore');
    url.searchParams.delete('mp_restore_failed');
    return url.toString();
  } catch (e) {
    let href = safeLocationHref();
    href = removeUrlParamFallback(href, 'auth_code_');
    href = removeUrlParamFallback(href, 'mp_restore');
    href = removeUrlParamFallback(href, 'mp_restore_failed');
    return href;
  }
}

function hasRestoreFailedFlag() {
  return hasUrlParam('mp_restore_failed');
}

function hasMiniAuthSyncedFlag() {
  return getUrlParam(MINI_AUTH_SYNCED_PARAM) === '1' || getUrlParam('mp_restore') === '1';
}

export function markMiniProgramRestoreApplied() {
  safeSessionSet(MINI_RESTORE_APPLIED_FLAG, '1');
}

export function clearMiniProgramRestoreApplied() {
  safeSessionRemove(MINI_RESTORE_APPLIED_FLAG);
}

export function isMiniProgramRestoreApplied() {
  if (safeLocationSearch().indexOf('mp_restore=1') > -1) return true;
  return safeSessionGet(MINI_RESTORE_APPLIED_FLAG) === '1';
}

export function syncAuthToMiniProgram(rawAuthCode, loginUser) {
  if (!rawAuthCode || !loginUser) return;

  isMiniProgram(isMini => {
    if (!isMini || !wx.miniProgram) return;
    reportH5MiniAuthState('H5_POST_AUTH_SYNC_SENT', {
      company_id_: loginUser.company_id_,
      mini_auth_exists_: 1
    });

    wx.miniProgram.postMessage({
      data: {
        type: 'YSDH_H5_AUTH_SYNC',
        version: 1,
        companyId: loginUser.company_id_,
        authCode: rawAuthCode,
        currentUrl: cleanCurrentUrl(),
        expiresIn: loginUser.expires_in,
        ts: Date.now()
      }
    });
  });
}

export function clearMiniProgramAuth(companyId) {
  isMiniProgram(isMini => {
    if (!isMini || !wx.miniProgram) return;
    reportH5MiniAuthState('H5_POST_AUTH_CLEAR_SENT', {
      company_id_: companyId,
      mini_auth_exists_: 0
    });

    wx.miniProgram.postMessage({
      data: {
        type: 'YSDH_H5_AUTH_CLEAR',
        companyId,
        ts: Date.now()
      }
    });
  });
}

export function tryRestoreFromMiniProgram() {
  const token = safeGet(SystemConst.token);
  const ysUser = safeGet(SystemConst.mShop_user);

  if (token && ysUser) return;
  if (!hasMiniAuthSyncedFlag()) return;

  if (hasRestoreFailedFlag()) {
    reportH5MiniAuthState('MINI_RESTORE_FAILED_FLAG_SEEN', {
      company_id_: safeGet(SystemConst.companyId),
      mini_auth_exists_: 0
    });
    console.log('tryRestoreFromMiniProgram===>mini restore failed flag seen, skip');
    return;
  }

  isMiniProgram(isMini => {
    if (!isMini || !wx.miniProgram) return;
    if (safeSessionGet(MINI_RESTORE_FLAG)) {
      reportH5MiniAuthState('MINI_RESTORE_ALREADY_TRIED', {
        company_id_: safeGet(SystemConst.companyId),
        mini_auth_exists_: 0
      });
      console.log('tryRestoreFromMiniProgram===>mini restore already tried');
      return;
    }

    safeSessionSet(MINI_RESTORE_FLAG, '1');
    reportH5MiniAuthState('MINI_RESTORE_REQUEST', {
      company_id_: safeGet(SystemConst.companyId),
      mini_auth_exists_: 0
    });
    console.log('tryRestoreFromMiniProgram===>request mini restore');

    const url = `${MINI_WEBVIEW_PAGE}?restore=1&target=${encodeURIComponent(cleanCurrentUrl())}&ts=${Date.now()}`;
    const navigateFallback = () => {
      if (wx.miniProgram.navigateTo) {
        wx.miniProgram.navigateTo({ url });
      }
    };

    try {
      wx.miniProgram.redirectTo({
        url,
        fail() {
          navigateFallback();
        }
      });
    } catch (e) {
      navigateFallback();
    }
  });
}



// WEBPACK FOOTER //
// ./src/utils/mini-program-auth-sync.js